// src/webideal-recruit/pages/Dashboard.jsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { motion } from "framer-motion";
import { Briefcase, Users, Calendar, TrendingUp } from "lucide-react";

import StatCard from "../components/dashboard/StatCard";
import PipelineChart from "../components/dashboard/PipelineChart";
import RecentActivity from "../components/dashboard/RecentActivity";
import ActiveJobs from "../components/dashboard/ActiveJobs";

import { Skeleton } from "@/components/ui/skeleton";

import { colors as DS, TechLabel, StatusBadge } from "../../design-system";

const ACCENT = DS.product.recruit;

export default function Dashboard() {
  const { data: jobs = [], isLoading: loadingJobs } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => backendApi.entities.Job.list("-created_date"),
  });

  const { data: applications = [], isLoading: loadingApplications } = useQuery({
    queryKey: ["applications"],
    queryFn: () => backendApi.entities.Application.list("-created_date"),
  });

  const { data: interviews = [], isLoading: loadingInterviews } = useQuery({
    queryKey: ["interviews"],
    queryFn: () => backendApi.entities.Interview.list("-scheduled_at"),
  });

  const isLoading = loadingJobs || loadingApplications || loadingInterviews;

  const activeJobs = jobs.filter((j) => j.status === "active").length;

  const candidates = [];
  applications.forEach((a) => {
    if (a.applicant && !candidates.find((c) => c.id === a.applicant._id)) {
      candidates.push({ id: a.applicant._id, ...a.applicant });
    }
  });
  const totalCandidates = candidates.length;

  const upcomingInterviews = interviews.filter(
    (i) => i.status === "scheduled" && new Date(i.scheduled_at) > new Date()
  ).length;

  const hiredThisMonth = applications.filter((a) => {
    const created = new Date(a.created_date);
    const now = new Date();
    return (
      a.stage === "hired" &&
      created.getMonth() === now.getMonth() &&
      created.getFullYear() === now.getFullYear()
    );
  }).length;

  const stats = [
    { title: "Active Jobs",          value: activeJobs,          icon: Briefcase,  color: "indigo"  },
    { title: "Total Candidates",     value: totalCandidates,     icon: Users,      color: "violet"  },
    { title: "Upcoming Interviews",  value: upcomingInterviews,  icon: Calendar,   color: "amber"   },
    { title: "Hired This Month",     value: hiredThisMonth,      icon: TrendingUp, color: "emerald" },
  ];

  if (isLoading) {
    return (
      <div className="space-y-8" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
        <div>
          <Skeleton className="h-5 w-32 mb-3 rounded-none" style={{ background: DS.hairline }} />
          <Skeleton className="h-10 w-72 mb-2 rounded-none" style={{ background: DS.hairline }} />
          <Skeleton className="h-5 w-96 rounded-none" style={{ background: DS.hairline }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: DS.hairline }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-none" style={{ background: DS.paper }} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[350px] rounded-none lg:col-span-2" style={{ background: DS.hairline }} />
          <Skeleton className="h-[350px] rounded-none" style={{ background: DS.hairline }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="space-y-8"
      style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: DS.forest }}
    >
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-end justify-between flex-wrap gap-4"
      >
        <div>
          <StatusBadge color={ACCENT}>Dashboard · live</StatusBadge>
          <h1
            className="mt-3 text-3xl md:text-4xl font-extrabold leading-[1.05] tracking-tight"
            style={{ color: DS.forest }}
          >
            Welcome back.
            <br />
            <span style={{ color: `${DS.forest}80` }}>Here's your hiring overview.</span>
          </h1>
        </div>
        <TechLabel style={{ color: `${DS.grid}99` }}>
          {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
        </TechLabel>
      </motion.div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <StatCard {...stat} />
          </motion.div>
        ))}
      </div>

      {/* MAIN SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <PipelineChart applications={applications} />
          <ActiveJobs jobs={jobs} applications={applications} />
        </div>
        <RecentActivity applications={applications} candidates={candidates} jobs={jobs} />
      </div>
    </div>
  );
}
